// ── Lightweight Solana wallet connect (Phantom / window.solana). ──
// Deliberately dependency-free: we only need the public key (base58) for identity +
// the hold-to-play access gate. No transactions are signed here. If no wallet is
// present we fall back to "demo (no wallet)" mode upstream (see access.ts).

export interface SolanaProvider {
  isPhantom?: boolean;
  publicKey?: { toString(): string } | null;
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect?(): Promise<void>;
  /**
   * Phantom-style message signing. Returns the detached signature bytes.
   * (Phantom: `signMessage(Uint8Array, 'utf8') → { signature: Uint8Array }`.)
   */
  signMessage?(message: Uint8Array, display?: 'utf8' | 'hex'): Promise<{ signature: Uint8Array }>;
}

declare global {
  interface Window {
    solana?: SolanaProvider;
    phantom?: { solana?: SolanaProvider };
  }
}

function getProvider(): SolanaProvider | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.phantom?.solana ?? window.solana ?? undefined;
}

/** True if any injected Solana wallet (Phantom etc.) is available. */
export function hasWallet(): boolean {
  return getProvider() !== undefined;
}

/**
 * The injected provider IF it can sign messages (Phantom etc.), else null.
 * Used by the wallet-signature auth flow; a URL-handoff / mobile player has no provider here,
 * so the caller simply stays tokenless. Never throws.
 */
export function signingProvider(): SolanaProvider | null {
  const provider = getProvider();
  return provider && typeof provider.signMessage === 'function' ? provider : null;
}

// Very light base58 sanity check (Solana pubkeys are 32-44 chars, base58 alphabet).
const BASE58_PUBKEY = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * Wallet handed off via the URL (`?wallet=<pubkey>`). The landing page connects the wallet
 * and forwards it here so the player does not have to reconnect. Returns null if absent or
 * malformed. Read once at module load so a later history change does not surprise the gate.
 */
export function walletFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('wallet');
    if (!raw) return null;
    const trimmed = raw.trim();
    return BASE58_PUBKEY.test(trimmed) ? trimmed : null;
  } catch {
    return null;
  }
}

const REF_STORAGE_KEY = 'mn_ref';

/**
 * The referrer wallet handed off via the landing's `?ref=<wallet>`. Read once and PERSISTED to
 * localStorage so it survives the wallet-connect redirect dance (the URL may be replaced before the
 * account is created). Returns the URL value if present + valid, else the persisted one, else null.
 */
export function refFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('ref');
    const trimmed = raw?.trim();
    if (trimmed && BASE58_PUBKEY.test(trimmed)) {
      try {
        window.localStorage.setItem(REF_STORAGE_KEY, trimmed);
      } catch {
        /* ignore */
      }
      return trimmed;
    }
    const stored = window.localStorage.getItem(REF_STORAGE_KEY)?.trim();
    return stored && BASE58_PUBKEY.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Clear the persisted referrer once it's been consumed (account created), so it can't re-apply. */
export function clearStoredRef(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REF_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Returns the connected wallet's base58 address, or null if not connected/available. */
export function currentWallet(): string | null {
  const provider = getProvider();
  const pk = provider?.publicKey;
  return pk ? pk.toString() : null;
}

/**
 * Prompt the user to connect their wallet. Resolves to the base58 address, or null
 * if no provider exists or the user rejected. Never throws — callers degrade to demo mode.
 */
export async function connectWallet(): Promise<string | null> {
  const provider = getProvider();
  if (!provider) return null;
  try {
    const res = await provider.connect();
    return res.publicKey.toString();
  } catch {
    return null;
  }
}

/** Silent reconnect for already-trusted sessions (no popup). Null if it can't. */
export async function eagerConnectWallet(): Promise<string | null> {
  const provider = getProvider();
  if (!provider) return null;
  try {
    const res = await provider.connect({ onlyIfTrusted: true });
    return res.publicKey.toString();
  } catch {
    return null;
  }
}

export async function disconnectWallet(): Promise<void> {
  const provider = getProvider();
  try {
    await provider?.disconnect?.();
  } catch {
    /* ignore */
  }
}
